import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@alchemer/ui/components/dialog';
import { Input } from '@alchemer/ui/components/input';
import { Button } from '@alchemer/ui/components/button';
import { toast } from '@alchemer/ui/components/sonner';
import { useMutation } from '@tanstack/react-query';
import { checkImpersonationAccessImpersonate } from '@/api/generated/endpoints/impersonation';
import { Apps } from '@/api/generated/schemas/apps';
import { useBoundStore } from '@/store';
import { tokenStorage } from '@/lib/tokenStorage';
import type { CheckImpersonationAccessImpersonateParams } from '@/api/generated/schemas/checkImpersonationAccessImpersonateParams';
import { tw } from '@/lib/tailwind';

interface ImpersonateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ImpersonateUserDialog({ isOpen, onClose }: ImpersonateUserDialogProps) {
  const [email, setEmail] = useState('');
  const selectedRegion = useBoundStore((state) => state.selectedRegion);
  const setImpersonatedUser = useBoundStore((state) => state.setImpersonatedUser);

  const impersonateMutation = useMutation({
    mutationFn: async (params: CheckImpersonationAccessImpersonateParams) => {
      const response = await checkImpersonationAccessImpersonate(params);
      return response;
    },
    onSuccess: async (data) => {
      if (data.allowed) {
        // Extract name from email (before @)
        const name = email.split('@')[0] || email;

        // Set impersonated user in store (will persist to localStorage)
        setImpersonatedUser({
          name,
          email,
        });

        // Clear SDK token from sessionStorage before reload
        tokenStorage.clearToken();

        // Reload the page - this will refetch SDK token with X-Alt-User-Context header
        window.location.reload();
      } else {
        toast.error('Impersonation not allowed', {
          description: data.reason || 'You do not have permission to impersonate this user.',
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to check impersonation access', {
        description: error.message || 'An error occurred while checking impersonation access.',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    impersonateMutation.mutate({
      email: email.trim(),
      app: Apps.dashboard,
      datacenter: selectedRegion,
    });
  };

  const handleClose = () => {
    if (!impersonateMutation.isPending) {
      setEmail('');
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-106.25"
        aria-describedby="impersonate-user-description"
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Impersonate a User</DialogTitle>
            <DialogDescription id="impersonate-user-description">
              Enter the email address of the user you want to impersonate.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label
                htmlFor="email"
                className={tw(
                  'text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                )}
              >
                User Email
              </label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={impersonateMutation.isPending}
                autoComplete="email"
                autoCapitalize="none"
                aria-label="User email address"
                className="placeholder:opacity-40"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={impersonateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={impersonateMutation.isPending}
            >
              {impersonateMutation.isPending ? 'Checking...' : 'Impersonate'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
